import * as APITypesV1 from "../../../../api/cur8/generated-types/v1/Api";
import { makePath } from "../../../../api/url";
import { parseAsJSON } from "../../../../lib/parse";
import { applyParams } from "../../../params";
import { applyPagination } from "../../pagination";
import { deleteJSON, getJSON, postJSON } from "../../request";
import { APIContext } from "../../types";

export type ScanAPI = ReturnType<typeof createScanAPI>;

type QueryParams = Partial<APITypesV1.ScanListByPatientParams>;

export type LinkingResultAnnotation = {
  id: string;
  patientId: string;
  physicalArtefactId: string | null;
  classification: APITypesV1.MoleClassification;
  data: APITypesV1.AnnotationData2D;
  targetUri: string;
  applicationSpecificTarget: string;
};

export function createScanAPI({
  buildURL,
  fetchJSON,
  fetchPagedJSON,
  fetchRaw,
}: APIContext) {
  function fetchBlob(blobURL: URL) {
    const request = new Request(blobURL.toString());
    return fetchRaw(request);
  }

  function fetchImmutableScan({
    patientId,
    scanId,
    scanVersion,
  }: {
    patientId: string;
    scanId: string;
    scanVersion: string | "latest";
  }) {
    const path = makePath([
      "api",
      "v1",
      "scan",
      patientId,
      scanId,
      "version",
      scanVersion,
    ]);
    const url = buildURL(path);
    const request = getJSON(url);
    return fetchJSON<APITypesV1.ImmutableScan>(request);
  }

  function fetchImmutableScanResult({
    patientId,
    scanId,
    scanVersion,
    resultName,
  }: {
    patientId: string;
    scanId: string;
    scanVersion: string;
    resultName: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "scan",
      patientId,
      scanId,
      "version",
      scanVersion,
      "result",
      resultName,
    ]);
    const url = buildURL(path);
    const request = getJSON(url);
    return fetchJSON<APITypesV1.ImmutableScanResult>(request);
  }

  function fetchImmutableScanBlob({
    patientId,
    scanId,
    scanVersion,
    resultName,
    path: filePath,
  }: {
    patientId: string;
    scanId: string;
    scanVersion: string | "latest";
    resultName: string;
    path: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "scan",
      patientId,
      scanId,
      "version",
      scanVersion,
      "result",
      resultName,
      "blob",
      filePath,
    ]);
    const url = buildURL(path);
    return fetchBlob(url);
  }

  function fetchLinkingResults({
    patientId,
    scanId,
    scanVersion,
  }: {
    patientId: string;
    scanId: string;
    scanVersion: string | "latest";
  }) {
    return fetchImmutableScanBlob({
      patientId,
      scanId,
      scanVersion,
      resultName: "linking_results",
      path: "annotations.json",
    }).result.then((response) => {
      return parseAsJSON<LinkingResultAnnotation[]>(response);
    });
  }

  function queryImmutableScans(
    opts: (
      | APITypesV1.ImmutableScanListScansByPatientParams
      | APITypesV1.ImmutableScanQueryAllScanVersionsParams
    ) & { patientId?: string }
  ) {
    const parts = ["api", "v1", "scan"];
    if ("patientId" in opts && opts.patientId != null) {
      parts.push(opts.patientId);
    }
    parts.push("query");

    const path = makePath(parts);
    const url = buildURL(path);

    applyParams(url.searchParams, {
      scanType: opts.scanType,
      state: opts.state,
      sourceUri: opts.sourceUri,
      latestOnly: "latestOnly" in opts ? opts.latestOnly : undefined,
    });
    applyPagination(url.searchParams, opts);

    const request = getJSON(url);
    return fetchPagedJSON<APITypesV1.ImmutableScan>(request);
  }

  function queryImmutableScansForPatient(
    opts: APITypesV1.ImmutableScanListScansByPatientParams
  ) {
    return queryImmutableScans(opts);
  }

  function createScanJournalNote(
    {
      patientId,
      scanId,
      scanVersion,
    }: {
      patientId: string;
      scanId: string;
      scanVersion: string;
    },
    payload: APITypesV1.CreateJournalNoteRequest
  ) {
    const path = makePath([
      "api",
      "v1",
      "scan",
      patientId,
      scanId,
      "version",
      scanVersion,
      "journal",
    ]);
    const url = buildURL(path);
    const request = postJSON(url, payload);
    return fetchJSON<APITypesV1.PatientDetails>(request);
  }

  function deleteScanJournalNote({
    patientId,
    scanId,
    scanVersion,
    journalNoteId,
  }: {
    patientId: string;
    scanId: string;
    scanVersion: string;
    journalNoteId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "scan",
      patientId,
      scanId,
      "version",
      scanVersion,
      "journal",
      journalNoteId,
    ]);
    const url = buildURL(path);
    const request = deleteJSON(url, undefined);
    return fetchRaw(request);
  }

  return {
    fetchImmutableScan,
    fetchImmutableScanResult,
    fetchLinkingResults,
    queryImmutableScans,
    queryImmutableScansForPatient,
    createScanJournalNote,
    deleteScanJournalNote,
  };
}
